import * as React from 'react'
import styled from 'styled-components'
import { Input } from '../frontend-lib/components/common/Input'
import { Button } from '../frontend-lib/components/common/Button'
import { TextArea } from '../frontend-lib/components/common/TextArea'
import { PersonOutline, Phone, MailOutline, Home, Hotel } from 'styled-icons/material'
import DateTimePicker from '../frontend-lib/components/common/DateTimePicker/ConciergeDateTimePicker'
import { ConciergeMenus, fetchConciergeMenu } from '../frontend-lib/types/conciergeMenu'
import { ConciergeHairTypes, fetchConciergeHairType } from '../frontend-lib/types/conciergeHairType'
import { ConciergeHairLengths, fetchConciergeHairLength } from '../frontend-lib/types/conciergeHairLength'
import { loadCatalogue } from '../frontend-lib/i18n'
import { I18nCatalogue } from '../frontend-lib/i18n/concierge/types'
import { colors, sizes } from '../frontend-lib/styleConstants'
import { Select } from '../frontend-lib/components/common/Select'
import { request } from '../frontend-lib/utils/request'
import { apiURL } from '../frontend-lib/utils/url'
import { apiRoutes, routeFactories } from '../frontend-lib/routes'
import { KamismaPageContext } from './_app'
import moment from 'moment'
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"

interface Props {
  readonly catalogue: I18nCatalogue
  readonly conciergeMenus: ConciergeMenus
  readonly conciergeHairTypes: ConciergeHairTypes
  readonly conciergeHairLengths: ConciergeHairLengths
}

interface State {
  readonly conciergeMenu: string
  readonly enterDateTime: string
  readonly firstName: string
  readonly lastName: string
  readonly phoneNumber: string
  readonly email: string
  readonly sex: string
  readonly conciergeHairType: string
  readonly conciergeHairLength: string
  readonly countryOfResidence: string
  readonly hotelName: string
  readonly requestMessage: string
  readonly errorMessage?: string
  readonly recaptcha_token: string
}

const Concierge = ({ catalogue, conciergeMenus, conciergeHairTypes, conciergeHairLengths }: Props) => {
  // const { me } = React.useContext(GlobalContext)
  const formRef = React.useRef<HTMLFormElement>(null)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [state, setState] = React.useState<State>({
    conciergeMenu: '',
    enterDateTime: moment(new Date()).format('YYYY/MM/DD HH:mm'),
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    requestMessage: '',
    sex: '',
    conciergeHairType: '',
    conciergeHairLength: '',
    countryOfResidence: '',
    hotelName: '',
    recaptcha_token: '',
  })

  const concierge_menu_options = [
    { value: '', label: catalogue.selectConciergeMenu },
    ...conciergeMenus.map(d => {
      return {
        value: d.id,
        label: d.name,
      }
    }),
  ]

  const concierge_hair_type_options = [
    { value: '', label: catalogue.selectConciergeHairType },
    ...conciergeHairTypes.map(d => {
      return {
        value: d.id,
        label: d.name,
      }
    }),
  ]

  const concierge_hair_length_options = [
    { value: '', label: catalogue.selectConciergeHairLength },
    ...conciergeHairLengths.map(d => {
      return {
        value: d.id,
        label: d.name,
      }
    }),
  ]

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (formRef.current) {
      formRef.current.querySelectorAll('input').forEach(el => el.blur())
    }
    contactConcierge()
  }

  const contactConcierge = async () => {

    if(!executeRecaptcha) {
      return setState({ ...state, errorMessage: 'error' })
    }

    const reCaptchaToken = await executeRecaptcha('concierge')

    const { conciergeMenu, enterDateTime, firstName, lastName, phoneNumber, email, requestMessage, sex, conciergeHairType, conciergeHairLength, countryOfResidence, hotelName, recaptcha_token } = state
    const params = { conciergeMenu, enterDateTime, firstName, lastName, phoneNumber, email, requestMessage, sex, conciergeHairType, conciergeHairLength, countryOfResidence, hotelName, recaptcha_token }
    params.recaptcha_token = reCaptchaToken

    const url = apiURL(apiRoutes.contactConcierge)
    const response = await request(url, {
      method: 'POST',
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      alert('It may have been sent by a bot. Please try sending again.')
      alert(response.status)
      switch (response.status) {
        case 400:
          return setState({ ...state, errorMessage: '400' })
        case 401:
          return setState({ ...state, errorMessage: '401' })
        case 409:
          return setState({ ...state, errorMessage: '409' })
        default:
          return setState({ ...state, errorMessage: 'error' })
      }
    } else {
      alert('Your inquiry has been successfully sent.')
      setState({ ...state, errorMessage: undefined })
      location.href = routeFactories.root.pathname
    }
  }

  function changedDateTimePicker(date: any) {
    const changedDateTime = moment(date).format('YYYY/MM/DD HH:mm')
    setState({
      ...state,
      enterDateTime: changedDateTime,
    })
  }

  return (
    <Form ref={formRef} onSubmit={onSubmit} data-test="form">
      <Section style={{ border: 'none', textAlign: 'center' }}>
        <SectionTitle style={{ border: 'none' }}>
          Your Booking Concierge
        </SectionTitle>
        <Description>
          Thank you for using KAMISMA BEAUTY's concierge service.
          Based on the information you have entered, we will suggest the suitable KAMI CHARISMA hair stylists and salons for you.
          You will receive an automatic confirmation email of your registration details.
          If you did not receive the confirmation email within 24 hours, please check your spam folder.
        </Description>
        <SectionTitle style={{ border: 'none' }}>
          Please enter menus<Required>※</Required>
        </SectionTitle>
        <Select
          name="conciergeMenu"
          fluid
          style={{ height: '40px' }}
          options={concierge_menu_options}
          onChange={e => setState({ ...state, conciergeMenu: e.currentTarget.value })}
          required
        />
        <SectionTitle style={{ border: 'none'}}>
          Please enter the date and time<Required>※</Required>
        </SectionTitle>
        <DateTimePicker
          locale={'en'}
          enterDateTime={new Date()}
          onChanged={changedDateTimePicker}
        />
      </Section>

      <Section style={{ border: 'none' }}>
        <SectionTitle style={{ border: 'none', textAlign: 'center' }}>
          Please enter customer infomation
        </SectionTitle>
        <Label>{'First name'}<Required>※</Required></Label>
        <Input
          type="text"
          name="firstName"
          className="input"
          placeholder={'Anna'}
          fluid
          iconRight={<PersonOutline size="24" />}
          onChange={e => setState({ ...state, firstName: e.currentTarget.value })}
          required
        />
        <Label>{'Last name'}<Required>※</Required></Label>
        <Input
          type="text"
          name="lastName"
          className="input"
          placeholder={'Kamisma'}
          fluid
          iconRight={<PersonOutline size="24" />}
          onChange={e => setState({ ...state, lastName: e.currentTarget.value })}
          required
        />
        <Label>{'phone number'}<Required>※</Required>(no hyphen)</Label>
        <Input
          type="text"
          name="phoneNumber"
          className="input"
          placeholder={'00011112222'}
          fluid
          iconRight={<Phone size="24" />}
          onChange={e => setState({ ...state, phoneNumber: e.currentTarget.value })}
          required
        />
        <Label>{'E-mail'}<Required>※</Required></Label>
        <Input
          type="email"
          name="email"
          className="input"
          placeholder={'anna@kamisma.com'}
          fluid
          iconRight={<MailOutline size="24" />}
          onChange={e => setState({ ...state, email: e.currentTarget.value })}
          required
        />
        <Label>{'Your Hair Type'}<Required>※</Required></Label>
        <Select
          name="conciergeHairType"
          fluid
          style={{ height: '40px' }}
          options={concierge_hair_type_options}
          onChange={e => setState({ ...state, conciergeHairType: e.currentTarget.value })}
          required
        />
        <Label>{'What hair length will you have?'}<Required>※</Required></Label>
        <Select
          name="conciergeHairLength"
          fluid
          style={{ height: '40px' }}
          options={concierge_hair_length_options}
          onChange={e => setState({ ...state, conciergeHairLength: e.currentTarget.value })}
          required
        />
        <Label>{'Country of Residence'}<Required>※</Required></Label>
        <Input
          type="text"
          name="countryOfResidence"
          className="input"
          placeholder={'Japan'}
          fluid
          iconRight={<Home size="24" />}
          onChange={e => setState({ ...state, countryOfResidence: e.currentTarget.value })}
          required
        />
        <Label>{'Hotel name'}</Label>
        <Input
          type="text"
          name="hotelName"
          className="input"
          placeholder={'GINZA hotel'}
          fluid
          iconRight={<Hotel size="24" />}
          onChange={e => setState({ ...state, hotelName: e.currentTarget.value })}
        />
      </Section>
      <Section style={{ border: 'none' }}>
        <Label>{'sex'}</Label>
        <Input
          type="radio"
          name="sex"
          className="input"
          fluid
          onChange={e => setState({ ...state, sex: e.currentTarget.value })}
          value='1'
          style={{ display: 'inline-block' }}
        />Female
        <Input
          type="radio"
          name="sex"
          className="input"
          fluid
          onChange={e => setState({ ...state, sex: e.currentTarget.value })}
          value='2'
          style={{ display: 'inline-block' }}
        />Male
        <Input
          type="radio"
          name="sex"
          className="input"
          fluid
          onChange={e => setState({ ...state, sex: e.currentTarget.value })}
          value='3'
          style={{ display: 'inline-block' }}
        />Others
        <Label>{'Request'}</Label>
        <TextArea name="requestMessage" onChange={e => setState({ ...state, requestMessage: e.currentTarget.value })}/>
      </Section>
      <p>Please make a reservation for a haircut and hair design in Japan here and enjoy.</p>
      <CancelPolicy><a href='../service-term-en#cancel-policy'>cancel policy</a></CancelPolicy>
      <ButtonWrapper>
          <Button type="submit" look="primary">
            send
          </Button>
      </ButtonWrapper>
    </Form>
  )
}

Concierge.getInitialProps = async ({ req, me }: KamismaPageContext) => {
  const catalogue = await loadCatalogue<I18nCatalogue>('concierge', me, req)
  const conciergeMenus = await fetchConciergeMenu(req)
  const conciergeHairTypes = await fetchConciergeHairType(req)
  const conciergeHairLengths = await fetchConciergeHairLength(req)
  return { catalogue, conciergeMenus, conciergeHairTypes, conciergeHairLengths }
}

export default Concierge

const Form = styled.form`
  max-width: calc(${sizes.pageMinWidth} * 2.0);
  min-width: ${sizes.pageMinWidth};
  padding: 1rem;
  margin: 0 auto;

  .input + .input {
    margin-top: 1rem;
  }
`

const Section = styled.div`
  border: 1px solid ${colors.gray300};
  border-radius: 8px;
  background: ${colors.white};
  margin: 1rem auto 0;
  max-width: 840px;
  width: 96%;

  &:last-child {
    margin: 1rem auto;
  }
`

const Description = styled.div`
  margin: 1rem auto 0;
  padding: 0.7rem;
  font-size: 0.9rem;
  border: 1px solid ${colors.gray400};
  border-radius: 8px;
  text-align: left;
`

const CancelPolicy = styled.div`
  margin: 0;
  padding: 0.1rem;
  font-size: 1.0rem;
  text-align: center;
`

const SectionTitle = styled.h2`
  margin: 0;
  padding: 1rem;
  font-size: 1.75rem;
  border-bottom: 1px solid ${colors.gray300};
`

const Label = styled.div`
  margin: 0.75rem 0 0.2rem 0;
  padding: 0;
  font-size: 0.8rem;
  font-weight: normal;
  color: ${colors.gray700};
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1.5rem;
`

const Required = styled.span`
  color: red;
  font-size: 0.9rem;
  font-size: 12px;
`
